<template>
  <Card class="modules-card">
    <CardHeader class="flex flex-row items-center justify-between gap-3">
      <CardTitle>Модули продукта</CardTitle>
      <Badge variant="secondary">{{ filteredModules.length }} разделов</Badge>
    </CardHeader>
    <CardContent>
      <label class="ui-field mb-5">
        <span class="ui-label">Поиск по названию модуля</span>
        <Input v-model="search" type="search" placeholder="Название модуля" />
      </label>
      <p v-if="filteredModules.length === 0" class="ui-empty">
        {{ search ? 'Модули по запросу не найдены' : 'У продукта нет модулей' }}
      </p>
      <div v-else class="space-y-3">
        <div v-if="leafModules.length" class="module-list">
          <article v-for="module in leafModules" :key="module.id" class="catalog-module-line">
            <span
              class="mark"
              :class="{ 'mark-paid': module.is_paid }"
              :title="module.is_paid ? 'Платный' : undefined"
            >
              <template v-if="module.is_paid">₽</template>
            </span>
            <strong class="module-name">{{ module.name }}</strong>
            <span class="module-hint">
              <Hint v-if="module.description" label="Описание">{{ module.description }}</Hint>
            </span>
          </article>
        </div>
        <details v-for="rootModule in groupModules" :key="rootModule.id" class="module-group-panel">
          <summary class="catalog-summary">
            <span class="module-name">{{ rootModule.name }}</span>
            <span
              class="mark"
              :class="{ 'mark-paid': rootModule.is_paid }"
              :title="rootModule.is_paid ? 'Платный' : undefined"
            >
              <template v-if="rootModule.is_paid">₽</template>
            </span>
            <Badge variant="outline" class="num">{{ rootModule.children.length }}</Badge>
          </summary>
          <div class="module-list catalog-module-children">
            <article
              v-for="childModule in rootModule.children"
              :key="childModule.id"
              class="catalog-module-line"
            >
              <span
                class="mark"
                :class="{ 'mark-paid': childModule.is_paid }"
                :title="childModule.is_paid ? 'Платный' : undefined"
              >
                <template v-if="childModule.is_paid">₽</template>
              </span>
              <strong class="module-name">{{ childModule.name }}</strong>
              <span class="module-hint">
                <Hint v-if="childModule.description" label="Описание">
                  {{ childModule.description }}
                </Hint>
              </span>
            </article>
          </div>
        </details>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Hint } from '@/components/ui/hint';
import { Input } from '@/components/ui/input';

import { computed, ref } from 'vue';

interface Props {
  modules: CatalogModule[];
}

const props = defineProps<Props>();

const search = ref('');

const filteredModules = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) {
    return props.modules;
  }

  return props.modules.reduce((acc: CatalogModule[], rootModule) => {
    const rootMatches = rootModule.name.toLowerCase().includes(query);
    const matchingChildren = rootModule.children.filter((childModule) =>
      childModule.name.toLowerCase().includes(query)
    );

    if (!rootMatches && matchingChildren.length === 0) {
      return acc;
    }

    acc.push({
      ...rootModule,
      children: rootMatches ? rootModule.children : matchingChildren,
    });
    return acc;
  }, []);
});

const groupModules = computed(() =>
  filteredModules.value.filter((module) => module.children.length > 0)
);
const leafModules = computed(() =>
  filteredModules.value.filter((module) => module.children.length === 0)
);
</script>
